import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { useSiteSection } from "@/hooks/useSiteContent";

export interface LangOption {
  code: string;
  name: string;
  flag: string;
  enabled: boolean;
}

// Basissprache der Website
export const BASE_LANG = "de";

export const ALL_LANGUAGES: LangOption[] = [
  { code: "de", name: "Deutsch", flag: "🇦🇹", enabled: true },
  { code: "en", name: "English", flag: "🇬🇧", enabled: false },
  { code: "fr", name: "Français", flag: "🇫🇷", enabled: false },
  { code: "hu", name: "Magyar", flag: "🇭🇺", enabled: false },
  { code: "ro", name: "Română", flag: "🇷🇴", enabled: false },
  { code: "es", name: "Español", flag: "🇪🇸", enabled: false },
  { code: "it", name: "Italiano", flag: "🇮🇹", enabled: false },
  { code: "ru", name: "Русский", flag: "🇷🇺", enabled: false },
  { code: "zh", name: "中文", flag: "🇨🇳", enabled: false },
  { code: "ar", name: "العربية", flag: "🇸🇦", enabled: false },
];

const TRANSLATE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/translate`;
const CACHE_KEY = "translation_cache_v2_de";
const CACHE_TS_KEY = "translation_cache_v2_de_ts";
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24h

type TranslationCache = Record<string, Record<string, string>>; // { lang: { srcText: translated } }

interface TranslationContextType {
  currentLang: string;
  setCurrentLang: (lang: string) => void;
  enabledLanguages: LangOption[];
  featureEnabled: boolean;
  t: (text: string) => string;
  isTranslating: boolean;
  translateBatch: (texts: string[]) => Promise<string[]>;
}

const TranslationContext = createContext<TranslationContextType>({
  currentLang: BASE_LANG,
  setCurrentLang: () => {},
  enabledLanguages: [],
  featureEnabled: false,
  t: (text) => text,
  isTranslating: false,
  translateBatch: async (texts) => texts,
});

export const useTranslation = () => useContext(TranslationContext);

function purgeLegacyCaches() {
  // Alte Übersetzungs-Caches (Basissprache war früher Serbisch) entfernen
  try {
    ["translation_cache", "translation_cache_ts", "translation_cache_v1", "lang", "currentLang"].forEach((k) =>
      localStorage.removeItem(k)
    );
  } catch { /* ignore */ }
}

function loadCache(): TranslationCache {
  purgeLegacyCaches();
  try {
    const ts = localStorage.getItem(CACHE_TS_KEY);
    if (ts && Date.now() - Number(ts) > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TS_KEY);
      return {};
    }
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveCache(cache: TranslationCache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    localStorage.setItem(CACHE_TS_KEY, String(Date.now()));
  } catch { /* ignore quota errors */ }
}

export const TranslationProvider = ({ children }: { children: ReactNode }) => {
  const { data: langSettings } = useSiteSection("language_settings");
  const [currentLang, setCurrentLang] = useState(BASE_LANG);
  const [cache, setCache] = useState<TranslationCache>(loadCache);
  const [isTranslating, setIsTranslating] = useState(false);
  const [pendingBatch, setPendingBatch] = useState<Set<string>>(new Set());

  const featureEnabled = !!langSettings?.enabled;

  const enabledLanguages: LangOption[] = ALL_LANGUAGES.filter((l) => {
    if (l.code === BASE_LANG) return true;
    if (!featureEnabled) return false;
    const enabledCodes: string[] = langSettings?.languages || [];
    return enabledCodes.includes(l.code);
  });

  // Save cache to localStorage whenever it changes
  useEffect(() => {
    saveCache(cache);
  }, [cache]);

  const translateBatch = useCallback(async (texts: string[]): Promise<string[]> => {
    if (currentLang === BASE_LANG) return texts;

    const langCache = cache[currentLang] || {};
    const uncached = texts.filter((t) => !langCache[t]);

    if (uncached.length === 0) {
      return texts.map((t) => langCache[t] || t);
    }

    setIsTranslating(true);
    try {
      const resp = await fetch(TRANSLATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ texts: uncached, targetLang: currentLang }),
      });

      if (!resp.ok) throw new Error("Translation failed");

      const { translations } = await resp.json();
      const newCache = { ...langCache };
      uncached.forEach((src, i) => {
        newCache[src] = translations[i] || src;
      });

      setCache((prev) => ({ ...prev, [currentLang]: newCache }));

      return texts.map((t) => newCache[t] || t);
    } catch (e) {
      console.error("Translation error:", e);
      return texts;
    } finally {
      setIsTranslating(false);
    }
  }, [currentLang, cache]);

  const t = useCallback((text: string): string => {
    if (currentLang === BASE_LANG || !text?.trim()) return text;
    const langCache = cache[currentLang] || {};
    if (langCache[text]) return langCache[text];

    // Queue for batch translation
    if (!pendingBatch.has(text)) {
      setPendingBatch((prev) => new Set(prev).add(text));
    }
    return text; // Return original until translated
  }, [currentLang, cache, pendingBatch]);

  // Process pending batch
  useEffect(() => {
    if (pendingBatch.size === 0 || currentLang === BASE_LANG || isTranslating) return;

    const timer = setTimeout(async () => {
      const texts = Array.from(pendingBatch);
      setPendingBatch(new Set());
      await translateBatch(texts);
    }, 100); // Debounce 100ms to collect texts

    return () => clearTimeout(timer);
  }, [pendingBatch, currentLang, isTranslating, translateBatch]);

  return (
    <TranslationContext.Provider value={{
      currentLang,
      setCurrentLang,
      enabledLanguages,
      featureEnabled,
      t,
      isTranslating,
      translateBatch,
    }}>
      {children}
    </TranslationContext.Provider>
  );
};
